/**
 * A transform gizmo with the plumbing every user of one needs: its helper in
 * the scene, the camera rig suspended while it drags, and a record of whether
 * the press that ends in a click began on one of its handles — so a tool's
 * click handler can tell a gizmo click from a click on the scene.
 */

import type { Camera, Object3D, Scene } from "three";
import { TransformControls } from "three/addons/controls/TransformControls.js";
import type { CameraRig } from "./cameraRig.ts";

export type GizmoEvents = Readonly<{
  dragStart?: () => void;
  /** The attached object moved. */
  change: () => void;
  dragEnd: () => void;
}>;

export class Gizmo {
  readonly controls: TransformControls;

  private readonly scene: Scene;
  private readonly domElement: HTMLElement;
  private readonly helper: Object3D;
  private active = false;
  private pressOn = false;

  constructor(scene: Scene, camera: Camera, domElement: HTMLElement, rig: CameraRig, events: GizmoEvents) {
    this.scene = scene;
    this.domElement = domElement;
    this.controls = new TransformControls(camera, domElement);
    this.controls.addEventListener("dragging-changed", (event) => {
      const dragging = event.value === true;
      rig.setEnabled(!dragging);
      if (dragging) {
        events.dragStart?.();
      } else {
        events.dragEnd();
      }
    });
    this.controls.addEventListener("objectChange", () => events.change());

    this.helper = this.controls.getHelper();
    scene.add(this.helper);
    // Capture phase, so the flag is set before any other pointer handler asks.
    domElement.addEventListener("pointerdown", this.onPointerDown, true);
    this.apply();
  }

  attach(object: Object3D | null): void {
    if (object) {
      this.controls.attach(object);
    } else {
      this.controls.detach();
    }
    this.apply();
  }

  /** Shown and answering the pointer only while its tool is active and something is attached. */
  setActive(active: boolean): void {
    this.active = active;
    this.apply();
  }

  private apply(): void {
    const on = this.active && this.controls.object !== undefined;
    this.controls.enabled = on;
    this.helper.visible = on;
  }

  isPointerOn(): boolean {
    return this.controls.enabled && this.controls.axis != null;
  }

  wasPressOn(): boolean {
    return this.pressOn;
  }

  private readonly onPointerDown = (): void => {
    this.pressOn = this.isPointerOn();
  };

  dispose(): void {
    this.domElement.removeEventListener("pointerdown", this.onPointerDown, true);
    this.scene.remove(this.helper);
    this.controls.dispose();
  }
}
